/*
 * @Author: 谭聪 156638174@qq.com
 * @Date: 2023-08-19 12:29:39
 * @LastEditors: liupan
 * @LastEditTime: 2023-12-22 15:36:15
 * @FilePath: /code/mall-fe/src/comps/ListRuleBar.tsx
 * @Description: 用于展示列表排序和筛选的规则栏
 *
 */
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
// import { Checkbox } from 'antd';
// import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { getFirstTownOfCityCode } from 'api/user';
import { ListData } from 'comps/ShopProListPC';
import useAreaCookie from 'hooks/useAreaCookie';
import GirdIcon from 'icons/shop/gird.svg';
import ListIcon from 'icons/shop/list.svg';
import jsurl from 'jsurl';
import { SiteContext, UserContext } from 'libs/contexts';
import { useRouter } from 'next/router';
import { ReactNode, useContext, useEffect, useState } from 'react';
import { cls } from 'reactutils';
import AreaCascader from './AreaCascader';
import { SortIcon } from './FilterPaneFixed';

type ListRuleBarProps = {
  listData: ListData;
  primaryColor?: string;
  updateSearch: Function;
  defaultSorter?: string;
  defaultOrderBy?: number;
  children?: ReactNode;
};

// 枚举，表示排序方式的顺序
export enum SorterOrder {
  ASC = 1, // 升序
  DESC = 2, // 降序
}

// 枚举，表示展示类型
export enum ShowType {
  GIRD = 1, // 网格视图
  LIST, // 列表视图
}

// 枚举，表示单选按钮的类型
export enum RadioType {
  ONLY_AGR = 'onlyShowAgreement', // 仅显示协议
  ALL_ADDRESS = 'showAllAddressId', // 显示所有地址
}

/**
 * React组件，用于展示列表排序和筛选的规则栏。
 *
 * @param {Object} props - 组件的属性对象。
 * @param {Array} props.listData - 列表数据。
 * @param {Function} props.updateSearch - 更新搜索条件的回调函数。
 * @param {string} [props.primaryColor='var(--primaryColor)'] - 主题色，默认为主题色变量。
 * @returns {JSX.Element} 渲染的React元素，包含列表排序和筛选的规则栏内容。
 */
export default function ListRuleBar({
  listData,
  updateSearch,
  primaryColor = 'var(--primaryColor)',
  defaultSorter = 'totalScore',
  defaultOrderBy = null,
  children,
}: ListRuleBarProps) {
  // 创建一个状态变量来存储当前的排序方式
  const [currentSorter, setCurrentSorter] = useState(defaultSorter);
  // 使用 useRouter 钩子从 Next.js 路由中获取路由信息
  const router = useRouter();
  // 解析路由参数，并将其转换为可操作的对象
  const params = jsurl.parse(router.query.params);
  const showType: ShowType = params?.showType ?? ShowType.GIRD;
  // 创建状态变量来存储价格范围的低值和高值
  const [priceRangeLow, setPriceRangeLow] = useState('');
  const [priceRangeHigh, setPriceRangeHigh] = useState('');
  // 创建状态变量来存储单选按钮的状态，使用 Record 类型来将 RadioType 映射到布尔值
  const [radioStatus, setRadioStatus] = useState<Record<RadioType, boolean>>({
    [RadioType.ALL_ADDRESS]: false,
    [RadioType.ONLY_AGR]: false,
  });
  const { areaSysNo, setCookie, AREA_CODE_KEY, AREA_SYS_NO_KEY } = useAreaCookie();
  const userInfo = useContext(UserContext);
  const site = useContext(SiteContext);

  // 使用 useEffect 钩子，处理在路由参数变化时的操作
  useEffect(() => {
    if (!router.query.params) {
      return;
    }
    const param = jsurl.parse(router.query.params);

    // 判断是否存在排序参数，并更新当前排序方式和价格范围
    if (param.sorts && param.sorts[0]) {
      const s = param.sorts?.[0];
      // 判断排序规则对象中是否存在有效的排序字段和排序顺序
      if (s.sortField && s.orderBy) {
        setCurrentSorter(s.sortField);
      }
      // 判断是否存在价格范围的最低值参数
      if (param.priceRangeLow) {
        setPriceRangeLow(param.priceRangeLow);
      }
      // 判断是否存在价格范围的最高值参数
      if (param.priceRangeHigh) {
        setPriceRangeHigh(param.priceRangeHigh);
      }
    } else {
      // 如果没有排序参数，将当前排序方式重置为空
      setCurrentSorter(defaultSorter);
    }

    // 更新单选按钮状态
    setRadioStatus({
      [RadioType.ONLY_AGR]: param.onlyShowAgreement ?? false,
      [RadioType.ALL_ADDRESS]: param.showAllAddressId ?? false,
    });
  }, [router.query.params, defaultSorter]);

  // 根据排序字段获取排序方式
  function getOrderBy(sortField: string) {
    const sortItem = params?.sorts?.find(
      (item: { sortField: any }) => item.sortField === sortField,
    );
    let defaultValue = null;
    if (!sortItem && defaultSorter === sortField) {
      defaultValue = defaultOrderBy;
    }
    return sortItem?.orderBy || defaultValue;
  }

  // 当排序方式发生变化时的处理函数
  const onSorterChange = (sortField, orderBy) => {
    const sortParam = [{ sortField, orderBy }];
    updateSearch({
      sorts: sortParam,
    });
  };

  // 获取排序样式类名
  function getSortClass(key) {
    return cls('sort-item', key, currentSorter === key && 'sort-down-active');
  }

  // 处理排序项点击事件
  function handleClickSortItem(key) {
    setCurrentSorter(key);
    onSorterChange(key, getOrderBy(key) === SorterOrder.ASC ? SorterOrder.DESC : SorterOrder.ASC);
  }

  // 处理前一页按钮点击事件
  function handlePre() {
    if (listData.pageNo === 1) return;
    updateSearch({
      pageNo: listData.pageNo - 1,
    });
  }

  // 处理后一页按钮点击事件
  function handleNext() {
    if (listData.pageNo === listData.totalPage) return;
    updateSearch({
      pageNo: listData.pageNo + 1,
    });
  }

  // 处理地区选择变化
  async function handleAreaChange(data: string[]) {
    if (!data || !data?.length) return;
    // 根据市 查询最后一级地区信息
    let res = await getFirstTownOfCityCode(data[data.length - 1]);
    if (res?.code === 200 && res?.data) {
      const { levelCode } = res.data;

      // 更新地区相关的状态和搜索参数
      setCookie(AREA_SYS_NO_KEY, res.data.sysNo);
      setCookie(AREA_CODE_KEY, levelCode);
      updateSearch({
        areaLevelCode: levelCode,
      });
    }
  }

  return (
    <>
      <div className="rule-bar">
        <div className="sort clearfix">
          {/* 排序选项 */}
          <ul className="sort-list clearfix">
            {/* 综合排序 */}
            <li className={getSortClass('totalScore')}>
              <a
                onClick={() => {
                  handleClickSortItem('totalScore');
                }}
              >
                <span>综合</span>
                <SortIcon orderBy={getOrderBy('totalScore')} />
              </a>
            </li>
            {/* 销量排序 */}
            <li className={getSortClass('sellCount')}>
              <a
                onClick={() => {
                  handleClickSortItem('sellCount');
                }}
              >
                <span>销量</span>
                <SortIcon orderBy={getOrderBy('sellCount')} />
              </a>
            </li>
            {/* 评分排序 */}
            <li className={getSortClass('shopScore')}>
              <a
                onClick={() => {
                  handleClickSortItem('shopScore');
                }}
              >
                <span>评分</span>
                <SortIcon orderBy={getOrderBy('shopScore')} />
              </a>
            </li>
          </ul>
          <ul className="type">
            <li className={cls('type-item type-gird', showType === ShowType.GIRD && 'active')}>
              <a
                onClick={() => {
                  updateSearch({
                    showType: ShowType.GIRD,
                  });
                }}
              >
                <span className="type-item-icon">
                  <GirdIcon />
                </span>
                <span className="type-item-txt">大图</span>
              </a>
            </li>
            <li className={cls('type-item type-gird', showType === ShowType.LIST && 'active')}>
              <a
                onClick={() => {
                  updateSearch({
                    showType: ShowType.LIST,
                  });
                }}
              >
                <span className="type-item-icon">
                  <ListIcon />
                </span>
                <span className="type-item-txt">列表</span>
              </a>
            </li>
            <li className="type-item pager">
              <div className="inline-pagination black">
                <span className="inline-pagination-number">
                  <span className="inline-pagination-current">{listData.pageNo}</span>
                  <span className="inline-pagination-separator"> / </span>
                  <span className="inline-pagination-all">{listData.totalPage}</span>
                </span>
                <span className="inline-pagination-trigger clearfix">
                  <a
                    className={cls(
                      'inline-pagination-trigger-prev',
                      listData.pageNo === 1 && 'disabled',
                    )}
                    onClick={handlePre}
                    title="上一页"
                  >
                    <LeftOutlined />
                  </a>
                  <a
                    onClick={handleNext}
                    className={cls(
                      'inline-pagination-trigger-next',
                      listData.pageNo === listData.totalPage && 'disabled',
                    )}
                    title="下一页"
                  >
                    <RightOutlined />
                  </a>
                </span>
              </div>
            </li>
          </ul>

          <div className="sort-price">
            <input
              type="tel"
              placeholder="最低价"
              maxLength={10}
              value={priceRangeLow}
              onInput={(e: any) => {
                setPriceRangeLow(e.target.value);
              }}
            />
            <span>-</span>
            <input
              type="tel"
              placeholder="最高价"
              maxLength={10}
              value={priceRangeHigh}
              onInput={(e: any) => {
                setPriceRangeHigh(e.target.value);
              }}
            />
            <a
              className="btn btn-primary"
              onClick={() => {
                updateSearch({
                  priceRangeLow,
                  priceRangeHigh,
                  ts: new Date().valueOf(),
                });
              }}
            >
              确定
            </a>
          </div>
        </div>
        <div className="rule clearfix">
          <div className="rule-item addr">
            <div className="text">配送至</div>
            <div
              className={cls(
                'cascader',
                'rule-item',
                radioStatus.showAllAddressId && 'disabled-cascader',
              )}
            >
              <AreaCascader
                placeholder="请选择"
                value={areaSysNo}
                areaLevel={3}
                separator={''}
                onChange={handleAreaChange}
                trigger="click"
                disabled={radioStatus.showAllAddressId}
              />
            </div>
          </div>

          {/* <div className="rule-checkbox">
            <Checkbox
              checked={radioStatus.showAllAddressId}
              onChange={(e: CheckboxChangeEvent) =>
                updateSearch({
                  [RadioType.ALL_ADDRESS]: e.target.checked,
                })
              }
            >
              <span className="check-txt">显示全区域商品</span>
            </Checkbox>
          </div> */}
          {children}
          {/* {userInfo && site?.mall?.enableSelectAllSkuOfBindAgreement === 0 && (
            <div className="rule-checkbox">
              <Checkbox
                checked={radioStatus.onlyShowAgreement}
                onChange={(e: CheckboxChangeEvent) =>
                  updateSearch({
                    [RadioType.ONLY_AGR]: e.target.checked,
                  })
                }
              >
                <span className="check-txt">
                  {userInfo.isInside ? '仅显示已绑定交易规则的商品' : '仅展示可购买商品'}
                </span>
              </Checkbox>
            </div>
          )} */}
        </div>
      </div>
      <style jsx>{`
        .rule-bar {
          margin-top: 20px;
          margin-bottom: 20px;
        }
        .sort {
          background-color: #f5f5f5;
          border-top: 1px solid #e8e8e8;
          border-right: 1px solid #e8e8e8;
        }
        .rule-bar .rule,
        .rule-bar .sort {
          height: 42px;
        }
        .sort-list {
          float: left;
        }
        .sort-item {
          float: left;
          width: 60px;
          height: 41px;
          color: #333;
          cursor: pointer;
          border-right: 1px solid #e8e8e8;
        }
        .sort-item.sort-down-active,
        .sort-item.sort-up-active {
          color: #fff;
          background-color: ${primaryColor};
          border-right: 1px solid ${primaryColor};
        }

        .sort .sort-item > a {
          display: inline-block;
          vertical-align: top;
          width: 100%;
          height: 100%;
          padding-top: 3px;
          text-align: center;
          white-space: nowrap;
          font-size: 0;
        }
        .sort a,
        .type a {
          color: inherit;
        }
        .sort .sort-item > a > span {
          font-size: 12px;
          display: inline-block;
          vertical-align: top;
          width: auto;
          height: 36px;
          line-height: 36px;
        }

        .rule-bar .sort .type {
          float: right;
        }

        .type-item.active,
        .type-item.category-item:hover {
          color: ${primaryColor};
          background-color: #fff;
        }

        .type .type-item {
          float: left;
          height: 41px;
          line-height: 41px;
          color: #333;
          border-left: 1px solid #e8e8e8;
        }

        .type .type-item > a {
          display: inline-block;
          vertical-align: top;
          width: 100%;
          height: 100%;
          padding: 0 15px;
        }

        .type .type-item span {
          margin-left: 4px;
        }

        .type-item.pager {
          padding-right: 20px;
        }

        .inline-pagination.black {
          color: #333;
        }
        .inline-pagination {
          color: #999;
          font-size: 12px;
          vertical-align: middle;
        }

        .inline-pagination-number {
          display: inline-block;
          width: 85px;
          text-align: center;
        }

        .inline-pagination-number,
        .inline-pagination-total {
          line-height: 1.5;
        }

        .inline-pagination-number > span {
          margin-left: 0 !important;
        }

        .inline-pagination.black .inline-pagination-trigger {
          background-color: #fff;
        }

        .inline-pagination-trigger {
          display: inline-block;
          line-height: 20px;
          border: 1px solid #e8e8e8;
        }

        .inline-pagination-trigger a.disabled {
          color: #c8c8c8;
          cursor: not-allowed;
          pointer-events: none;
        }

        .inline-pagination-trigger a {
          display: inline-block;
          width: 24px;
          line-height: 24px;
          text-align: center;
          color: #999;
          cursor: pointer;
        }

        .inline-pagination-trigger-prev {
          border-right: 1px solid #e8e8e8;
        }

        .sort-price {
          float: left;
          margin-left: 60px;
          height: 41px;
          padding-top: 8px;
          padding-left: 10px;
          padding-right: 10px;
          font-size: 0;
          white-space: nowrap;
        }

        .sort-price input {
          display: inline-block;
          vertical-align: top;
          width: 68px;
          height: 24px;
          line-height: 22px;
          padding: 0 8px;
          font-size: 12px;
          border: 1px solid #e8e8e8;
        }

        .sort-price span {
          display: inline-block;
          vertical-align: top;
          line-height: 24px;
          padding: 0 7px;
          font-size: 12px;
        }

        .sort-price .btn {
          display: none;
          width: 40px;
          height: 24px;
          font-size: 12px;
          padding: 0;
          margin-left: 20px;
          line-height: 24px;
          color: #fff;
          vertical-align: top;
          text-align: center;
          background-color: ${primaryColor};
        }

        .sort-item :global(.sortIcon) {
          margin-right: 0;
          padding-top: 12px;
          margin-left: 4px;
        }

        .sort-item :global(.triangleBottom.active) {
          border-top-color: #fff;
        }

        .sort-item :global(.triangleTop.active) {
          border-bottom-color: #fff;
        }

        .sort-price:hover {
          background-color: #fff;
        }

        .sort-price:hover .btn {
          display: inline-block;
        }

        .type-item-icon :global(svg) {
          width: 12px;
          height: 12px;
        }

        .type-item-icon :global(svg path) {
          fill: #999;
        }

        .type-item.active :global(svg path) {
          fill: ${primaryColor};
        }
        .type-item.active .type-item-txt {
          color: ${primaryColor};
        }

        .rule-bar .rule {
          width: 100%;
          border: 1px solid #e8e8e8;
          display: flex;
          align-items: center;
        }

        .rule-bar .rule .rule-item.addr {
          padding: 7px 20px;
          display: flex;
          align-items: center;
          font-size: 12px;
        }

        .rule-item.addr .text {
          margin-right: 8px;
          color: #999;
          font-size: 12px;
        }

        .cascader {
          position: relative;
          display: inline-block;
          min-width: 150px;
          border: 1px solid #e8e8e8;
          vertical-align: middle;
          padding-left: 8px;
          line-height: 24px;
        }
        .disabled-cascader {
          background-color: #e4e7ed;
          cursor: not-allowed;
        }
        .check-txt {
          font-size: 12px;
        }

        .rule-checkbox :global(.ant-checkbox-checked::after) {
          border-color: ${primaryColor};
        }

        .rule-checkbox :global(.ant-checkbox-checked .ant-checkbox-inner) {
          background-color: ${primaryColor};
          border-color: ${primaryColor};
        }

        .rule-checkbox
          :global(
            .ant-checkbox-wrapper:hover .ant-checkbox-inner,
            .ant-checkbox:hover .ant-checkbox-inner,
            .ant-checkbox-input:focus + .ant-checkbox-inner
          ) {
          border-color: ${primaryColor};
        }
      `}</style>
    </>
  );
}
